import { useTranslations } from '@/hooks/useTranslations';
import { formatDateTime } from '@/lib/adoc/format';
import type { AdocSignature } from '@/types/adoc';
import { Info, PenLine } from 'lucide-react';

interface AdocSignaturePanelProps {
    signatures: AdocSignature[];
}

export function AdocSignaturePanel({ signatures }: AdocSignaturePanelProps) {
    const { t } = useTranslations();

    return (
        <div className="bg-card rounded-xl border">
            <div className="border-b px-4 py-3">
                <h2 className="font-semibold">{t('adoc.show.signatures.title', 'Signatures')}</h2>
            </div>

            {signatures.length === 0 ? (
                <p className="text-muted-foreground px-4 py-4 text-sm">{t('adoc.show.signatures.none', 'This container declares no signatures.')}</p>
            ) : (
                <div className="divide-y">
                    {signatures.map((signature, index) => (
                        <div key={index} className="flex items-start gap-3 px-4 py-3">
                            <div className="bg-accent text-accent-foreground flex size-9 shrink-0 items-center justify-center rounded-lg [&>svg]:size-4">
                                <PenLine />
                            </div>
                            <div className="min-w-0 flex-1 text-sm">
                                <div className="font-medium">{signature.signer ?? '—'}</div>
                                {signature.position ? <div className="text-muted-foreground">{signature.position}</div> : null}
                                <div className="text-muted-foreground mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-xs">
                                    <span>
                                        {t('adoc.show.signatures.signed_at', 'Signed')}: {formatDateTime(signature.signed_at)}
                                    </span>
                                    {signature.purpose ? (
                                        <span>
                                            {t('adoc.show.signatures.purpose', 'Purpose')}: {signature.purpose}
                                        </span>
                                    ) : null}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* The app reads the container's own metadata; it does not validate the
                XAdES cryptography. Saying so is the honest framing. */}
            <div className="text-muted-foreground flex items-start gap-2 border-t px-4 py-2.5 text-xs">
                <Info className="mt-0.5 size-3.5 shrink-0" />
                <span>
                    {t(
                        'adoc.show.signatures.disclaimer',
                        'Shown as declared inside the container. Signatures are not cryptographically verified here.',
                    )}
                </span>
            </div>
        </div>
    );
}
